import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePermissions } from '../../../hooks/usePermissions';
import { getGuardrailsGroups, getGuardrailsPolicies } from '../../../services/cloudopsApi';
import { FiShield as Shield, FiPlus as Plus } from 'react-icons/fi';
import { Card, CardContent } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Select } from '../../../components/ui/Select';
import GuardrailCard from '../components/GuardrailCard';

interface PolicyGroup {
  id: string;
  name: string;
}

interface Policy {
  id: string;
  name: string;
  category: string;
  is_active: boolean;
  group_ids?: string[];
  [key: string]: unknown;
}

interface PolicyPaginationMeta {
  total?: number;
  totalPages?: number;
  page?: number;
  limit?: number;
}

const PoliciesPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAdmin } = usePermissions();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [groups, setGroups] = useState<PolicyGroup[]>([]);
  const [paginationMeta, setPaginationMeta] = useState<PolicyPaginationMeta | null>(null);

  // Filters
  const [categoryFilter, setCategoryFilter] = useState<string>(searchParams.get('category') || 'all');
  const [resourceTypeFilter, setResourceTypeFilter] = useState<string>(searchParams.get('resource_type') || 'all');
  const [activeFilter, setActiveFilter] = useState<string>(searchParams.get('status') || 'all');
  const [groupFilter, setGroupFilter] = useState<string>(searchParams.get('group') || 'all');

  // Pagination
  const [currentPage, setCurrentPage] = useState<number>(Number.parseInt(searchParams.get('page') || '1', 10));
  const [pageSize, setPageSize] = useState<number>(Number.parseInt(searchParams.get('limit') || '12', 10));

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [categoryFilter, resourceTypeFilter, groupFilter]);

  // Sync URL with filters and pagination
  useEffect(() => {
    const params = new URLSearchParams();
    if (categoryFilter !== 'all') params.set('category', categoryFilter);
    if (resourceTypeFilter !== 'all') params.set('resource_type', resourceTypeFilter);
    if (activeFilter !== 'all') params.set('status', activeFilter);
    if (groupFilter !== 'all') params.set('group', groupFilter);
    if (currentPage > 1) params.set('page', String(currentPage));
    if (pageSize !== 12) params.set('limit', String(pageSize));
    setSearchParams(params, { replace: true });
  }, [categoryFilter, resourceTypeFilter, activeFilter, groupFilter, currentPage, pageSize, setSearchParams]);

  const loadPolicies = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getGuardrailsPolicies(
        categoryFilter === 'all' ? undefined : categoryFilter,
        resourceTypeFilter === 'all' ? undefined : resourceTypeFilter,
        currentPage,
        pageSize,
        groupFilter === 'all' ? undefined : groupFilter
      );
      const groupsResponse = await getGuardrailsGroups().catch(() => []);
      const data = response?.data ?? (Array.isArray(response) ? response : []);
      const meta = response?.meta ?? null;
      setPolicies(data);
      setPaginationMeta(meta);
      setGroups(groupsResponse || []);
    } catch (err: unknown) {
      console.error('Error loading policies:', err);
      setError(err instanceof Error ? err.message : 'Failed to load guardrail policies');
      setPolicies([]);
      setPaginationMeta(null);
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, [categoryFilter, resourceTypeFilter, groupFilter, currentPage, pageSize]);

  useEffect(() => {
    loadPolicies();
  }, [loadPolicies]);

  const filteredPolicies = policies.filter(p => {
    if (activeFilter === 'active' && !p.is_active) return false;
    if (activeFilter === 'inactive' && p.is_active) return false;
    return true;
  });

  const groupMap = groups.reduce<Record<string, string>>((acc, group) => {
    acc[group.id] = group.name;
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Guardrail Policies
            </h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              Manage compliance policies and rules
            </p>
          </div>
          {isAdmin && (
              <Button
                onClick={() => navigate('/cloudops/guardrails/policies/new')}
                variant="primary"
                className="flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                New Policy
              </Button>
          )}
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200">{error}</p>
          <Button
            onClick={loadPolicies}
            variant="ghost"
            size="sm"
            className="mt-2"
          >
            Try again
          </Button>
        </div>
      )}

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Select
              label="Category"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              options={[
                { value: 'all', label: 'All Categories' },
                { value: 'security', label: 'Security' },
                { value: 'cost', label: 'Cost Management' },
                { value: 'governance', label: 'Governance' },
                { value: 'reliability', label: 'Reliability' },
              ]}
            />
            <Select
              label="Resource Type"
              value={resourceTypeFilter}
              onChange={(e) => setResourceTypeFilter(e.target.value)}
              options={[
                { value: 'all', label: 'All Resource Types' },
                { value: 'ec2', label: 'EC2' },
                { value: 's3', label: 'S3' },
                { value: 'rds', label: 'RDS' },
                { value: 'lambda', label: 'Lambda' },
                { value: 'iam', label: 'IAM' },
                { value: 'security_group', label: 'Security Group' },
                { value: 'vpc', label: 'VPC' },
                { value: 'cloudtrail', label: 'CloudTrail' },
              ]}
            />
            <Select
              label="Status"
              value={activeFilter}
              onChange={(e) => setActiveFilter(e.target.value)}
              options={[
                { value: 'all', label: 'All' },
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Inactive' },
              ]}
            />
            <Select
              label="Group"
              value={groupFilter}
              onChange={(e) => setGroupFilter(e.target.value)}
              options={[
                { value: 'all', label: 'All Groups' },
                ...groups.map((group) => ({
                  value: group.id,
                  label: group.name,
                })),
              ]}
            />
          </div>
        </CardContent>
      </Card>

      {/* Policies Grid */}
      {filteredPolicies.length > 0 ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPolicies.map((policy) => {
              const groupNames = (policy.group_ids || [])
                .map((id) => groupMap[id])
                .filter(Boolean);
              return (
                <GuardrailCard
                  key={policy.id}
                  policy={policy}
                  groupNames={groupNames}
                  onEdit={() => navigate(`/cloudops/guardrails/policies/${policy.id}/edit`)}
                  onDelete={loadPolicies}
                />
              );
            })}
          </div>

          {/* Pagination */}
          {paginationMeta && ((paginationMeta.totalPages ?? 0) > 1 || (paginationMeta.total ?? 0) > pageSize) && (
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-gray-200 dark:border-gray-700 pt-6">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">Show</span>
                <Select
                  label=""
                  value={String(pageSize)}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  options={[
                    { value: '10', label: '10' },
                    { value: '12', label: '12' },
                    { value: '20', label: '20' },
                    { value: '50', label: '50' },
                  ]}
                  placeholder=""
                  className="w-20"
                />
                <span className="text-sm text-gray-600 dark:text-gray-400">per page</span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage <= 1}
                >
                  Previous
                </Button>
                <span className="text-sm text-gray-700 dark:text-gray-300 px-3">
                  Page {currentPage} of {paginationMeta.totalPages || 1}
                  {paginationMeta.total != null && (
                    <span className="text-gray-500 dark:text-gray-400 ml-1">
                      ({paginationMeta.total} total)
                    </span>
                  )}
                </span>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setCurrentPage((p) => Math.min(paginationMeta.totalPages || 1, p + 1))}
                  disabled={currentPage >= (paginationMeta.totalPages || 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <Shield className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              No policies found
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {policies.length === 0
                ? 'Create your first guardrail policy to start monitoring compliance'
                : 'No policies match your current filters'}
            </p>
            {isAdmin && (
              <Button
                onClick={() => navigate('/cloudops/guardrails/policies/new')}
                variant="primary"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Policy
              </Button>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PoliciesPage;

